import React, { useContext } from "react";
import { AppContext, Layer } from "../../../context/context";
import { useEditLayer } from "../../layer_panel/functions/layerFunctions";
import { Point, Shape } from "../../../context/context";
import { Marker } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { LatLng, DivIcon } from "leaflet";
import { logEvent } from '../../../services/analytics';
import { liftangoBrandTokens } from '../../../theme';
import { getResolvedMissionControlPoint } from './missionControlTripStyles';

// Layers with more than this many point shapes are wrapped in a MarkerClusterGroup
// when rendered on the map. Bus-stop layers from OSM can contain hundreds or
// thousands of points and become unusable as individual SVG markers.
const CLUSTER_THRESHOLD = 250;

export function dispatchFixedRouteCaptureLeftClick(lat: number, lng: number) {
    window.dispatchEvent(new CustomEvent('fixedRouteCaptureLeftClick', {
        detail: { lat, lng }
    }));
}

export function useAddPoint() {
    const context = useContext(AppContext);
    const editLayer = useEditLayer();

    if (!context) {
        throw new Error("useAddPoint must be used within the context provider")
    }

    const {global} = context;

    const {activeLayerId, mapLayers} = global;

    return (newPoint: Point) => {
        // If no active layer, select the topmost (first) layer
        let newActiveLayerId: number;
        if (mapLayers.length === 0) {
            // No layers exist, do nothing or throw error
            return;
        }
        if (activeLayerId == null || mapLayers.find(l => l.layerId === activeLayerId) == null) {
            newActiveLayerId = mapLayers[0].layerId;
            global.setActiveLayerId(mapLayers[0].layerId);
        } else {
            newActiveLayerId = activeLayerId as number;
        }

        if (newPoint.name === "") {
            newPoint.name = `New Point`
        }

        if (!newPoint.layerId) {
            newPoint.layerId = newActiveLayerId
        }

        // --- Analytics: Log point creation ---
        logEvent({
            category: 'Shape',
            action: 'CreatePoint',
            label: 'Point Created',
            vertexCount: 1, // Points always have 1 vertex
            coordinates: newPoint.coordinates,
            name: newPoint.name
        });

        const layer: Layer = mapLayers.filter(l => l.layerId === newActiveLayerId)[0]
        const newShapes: Shape[] = [...layer.shapes, newPoint]
        const newLayer: Layer = {
            ...layer,
            shapes: newShapes
        }

        editLayer(newLayer, newActiveLayerId)
    }
}

function getIconSvgPath(iconType: string): string {
    switch (iconType) {
        case 'default':
        case 'location':
            return 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z';
        case 'star':
            return 'M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z';
        case 'flag':
            return 'M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z';
        case 'hospital':
            return 'M19 3H5c-1.1 0-1.99.9-1.99 2L3 19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-8.5-2h3v-3.5H17v-3h-3.5V7h-3v3.5H7v3h3.5z';
        case 'school':
            return 'M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z';
        case 'shopping':
            return 'M15.55 13c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.37-.66-.11-1.48-.87-1.48H5.21l-.94-2H1v2h2l3.6 7.59-1.35 2.44C4.52 15.37 5.48 17 7 17h12v-2H7l1.1-2h7.45zM6.16 6h12.15l-2.76 5H8.53L6.16 6zM7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z';
        case 'airport':
            return 'M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z';
        case 'bus':
            return 'M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4s-8 .5-8 4v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6H6V6h12v5z';
        case 'train':
            return 'M12 2c-4 0-8 .5-8 4v9.5C4 17.43 5.57 19 7.5 19L6 20.5v.5h2.23l2-2H14l2 2h2v-.5L16.5 19c1.93 0 3.5-1.57 3.5-3.5V6c0-3.5-3.58-4-8-4zM7.5 17c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm3.5-7H6V6h5v4zm2 0V6h5v4h-5zm3.5 7c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z';
        case 'tram':
            return 'M19 16.94V8.5c0-2.79-2.61-3.4-6.01-3.49l.76-1.51H17V2H7v1.5h4.75l-.76 1.52C7.86 5.11 5 5.73 5 8.5v8.44c0 1.45 1.19 2.66 2.59 2.97L6 21.5v.5h2.23l2-2H14l2 2h2v-.5L16.5 20h-.08c1.38-.31 2.58-1.52 2.58-3.06zm-7 1.56c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm5-4.5H7V9h10v5z';
        case 'subway':
            return 'M12 2c-4.42 0-8 .5-8 4v9.5C4 17.43 5.57 19 7.5 19L6 20v1h2l2-2h4l2 2h2v-1l-1.5-1c1.93 0 3.5-1.57 3.5-3.5V6c0-3.5-3.58-4-8-4zM7.5 17c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm3.5-7H6V6h5v4zm2 0V6h5v4h-5zm3.5 7c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z';
        case 'store':
            return 'M20 4H4v2h16V4zm1 10v-2l-1-5H4l-1 5v2h1v6h10v-6h4v6h2v-6h1zm-9 4H6v-4h6v4z';
        case 'restaurant':
            return 'M16 6v8h3v8h2V2c-2.76 0-5 2.24-5 4zm-5 3H9V2H7v7H5V2H3v7c0 2.21 1.79 4 4 4v9h2v-9c2.21 0 4-1.79 4-4V2h-2v7z';
        case 'parking':
            return 'M13 3H6v18h4v-6h3c3.31 0 6-2.69 6-6s-2.69-6-6-6zm.2 8H10V7h3.2c1.1 0 2 .9 2 2s-.9 2-2 2z';
        default:
            return 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z';
    }
}

export function customMarker(
    point: Point,
    isSelected: boolean,
    options?: { isTripPointLinked?: boolean; allowColorFallback?: boolean }
): DivIcon {
    const resolvedPoint = getResolvedMissionControlPoint(
        point,
        options?.isTripPointLinked ?? false,
        options?.allowColorFallback ?? true
    );
    const isQuickStartHub = point.creationSource === 'quick-start-hub';
    const isMeetingPoint = /meeting point/i.test(`${point.name ?? ''} ${point.description ?? ''}`);
    const baseSize = resolvedPoint.size; // Use normalized size when remapped
    const size = isQuickStartHub
        ? Math.max(40, Math.round(baseSize * 1.6))
        : isMeetingPoint
            ? Math.max(24, Math.min(28, Math.round(baseSize * 1.05)))
            : baseSize;
    const iconType = resolvedPoint.icon || 'default';
    const resolvedColour = resolvedPoint.color;
    const isDot = iconType === 'dot';
    const dotCore = Math.max(isMeetingPoint ? 8 : 6, Math.round(size * (isMeetingPoint ? 0.68 : 0.66)));
    const iconFillColor = isMeetingPoint
        ? resolvedColour
        : (isQuickStartHub && (iconType === 'location' || iconType === 'default' || iconType === 'star'))
            ? '#ffffff'
            : resolvedColour;
    const hubOutline = isQuickStartHub
        ? '2.5px solid rgba(255,255,255,0.98)'
        : isMeetingPoint
            ? '2.5px solid rgba(255,255,255,0.98)'
            : 'none';
    const hubHalo = isQuickStartHub
        ? '0 0 0 2px rgba(13,71,161,0.95), 0 4px 12px rgba(0,0,0,0.3)'
        : isMeetingPoint
            ? `0 0 0 2px rgba(255,255,255,0.95), 0 0 0 4px ${resolvedColour}, 0 3px 10px rgba(0,0,0,0.28)`
        : undefined;
    const dotStroke = resolvedColour === liftangoBrandTokens.missionControl.tripDropoff
        ? 'rgba(255,255,255,0.9)'
        : 'rgba(58,58,72,0.28)';
    const iconHtml = isDot
        ? `
        <div style="
            display:flex;
            align-items:center;
            justify-content:center;
            width:${size}px;
            height:${size}px;
        ">
            <div style="
                width:${dotCore}px;
                height:${dotCore}px;
                border-radius:50%;
                background:${resolvedColour};
                border:${isQuickStartHub
                    ? '2.5px solid rgba(255,255,255,0.98)'
                    : isMeetingPoint
                        ? '2px solid rgba(255,255,255,0.98)'
                        : `1px solid ${dotStroke}`};
                box-shadow:${isSelected
                    ? `0 0 0 2px rgba(255,255,255,0.95), 0 0 0 4px ${liftangoBrandTokens.actionBrand.primary}, 0 2px 8px rgba(0,0,0,0.18)`
                    : isQuickStartHub
                        ? '0 0 0 2px rgba(13,71,161,0.92), 0 3px 10px rgba(0,0,0,0.28)'
                        : isMeetingPoint
                            ? `0 0 0 2px rgba(255,255,255,0.95), 0 0 0 4px ${resolvedColour}, 0 3px 10px rgba(0,0,0,0.28)`
                        : '0 1px 4px rgba(0,0,0,0.16)'};
            "></div>
        </div>
        `
        : `
        <div style="
            display: flex;
            align-items: center;
            justify-content: center;
            background: ${isQuickStartHub ? 'rgba(13,71,161,0.92)' : isMeetingPoint ? 'rgba(255,255,255,0.98)' : 'transparent'};
            border: ${hubOutline};
            border-radius: 999px;
            box-shadow: ${hubHalo || 'none'};
            width: ${size}px;
            height: ${size}px;
        ">
            <svg viewBox="0 0 24 24" style="width: 100%; height: 100%; fill: ${iconFillColor}; ${isSelected ? 'filter: drop-shadow(0 0 2px rgba(238,115,52,0.8));' : ''}">
                <path d="${getIconSvgPath(iconType)}"/>
            </svg>
        </div>
    `;

    // Calculate anchor point based on icon size to keep it centered
    const anchorPoint = size / 2;

    return new DivIcon({
        className: 'custom-marker',
        html: iconHtml,
        iconSize: [size, size],
        iconAnchor: [anchorPoint, anchorPoint]
    });
}

const PointMarker: React.FC<{
    point: Point;
    layer: Layer;
    layerIndex: number;
    pointIndex: number;
    isSelected: boolean;
    editLayer: Function;
    setSelectedShapes: Function;
    setContextMenu: Function;
    isTripPointLinked: boolean;
    allowColorFallback: boolean;
    isFixedRouteCaptureMode: boolean;
}> = ({ point, layer, layerIndex, pointIndex, isSelected, editLayer, setSelectedShapes, setContextMenu, isTripPointLinked, allowColorFallback, isFixedRouteCaptureMode }) => {
    const isMeetingPoint = /meeting point/i.test(`${point.name ?? ''} ${point.description ?? ''}`);
    const isQuickStartHub = point.creationSource === 'quick-start-hub';
    const handleContextMenu = (e: any) => {
        e.originalEvent.preventDefault();
        setContextMenu({
            x: e.originalEvent.pageX,
            y: e.originalEvent.pageY,
            point: point
        });
    };

    const handleClick = (e: any) => {
        if (isFixedRouteCaptureMode) {
            e.originalEvent.preventDefault();
            if (e.originalEvent.stopPropagation) {
                e.originalEvent.stopPropagation();
            }
            const [lat, lng] = point.coordinates;
            dispatchFixedRouteCaptureLeftClick(lat, lng);
            return;
        }

        e.originalEvent.preventDefault();
        if (e.originalEvent.ctrlKey) {
            // Multi-select with Ctrl key
            setSelectedShapes((prev: string[]) => {
                if (prev.includes(point.id)) {
                    return prev.filter(id => id !== point.id);
                } else {
                    return [...prev, point.id];
                }
            });
        } else {
            // Single select without Ctrl key
            setSelectedShapes([point.id]);
        }
    };

    const handleDragEnd = (e: any) => {
        const newPoint = {
            ...point,
            coordinates: [e.target.getLatLng().lat, e.target.getLatLng().lng]
        };
        const updatedShapes = layer.shapes.map(shape => 
            shape.id === point.id ? newPoint : shape
        );
        const updatedLayer = {
            ...layer,
            shapes: updatedShapes
        };
        editLayer(updatedLayer, layer.layerId);
    };

    return (
        <Marker
            key={`point-${point.id}`}
            position={new LatLng(point.coordinates[0], point.coordinates[1])}
            icon={customMarker(point, isSelected, { isTripPointLinked, allowColorFallback })}
            draggable={true}
            zIndexOffset={isMeetingPoint ? 50 : isQuickStartHub ? 900 : 0}
            eventHandlers={{
                contextmenu: handleContextMenu,
                click: handleClick,
                dragend: handleDragEnd
            }}
        />
    );
};

function returnPointMarker(
    point: Point,
    layer: Layer,
    layerIndex: number,
    pointIndex: number,
    isSelected: boolean,
    editLayer: Function,
    setSelectedShapes: Function,
    setContextMenu: Function,
    isTripPointLinked: boolean,
    allowColorFallback: boolean,
    isFixedRouteCaptureMode: boolean
) {
    return (
        <PointMarker
            key={`point-${point.id}`}
            point={point}
            layer={layer}
            layerIndex={layerIndex}
            pointIndex={pointIndex}
            isSelected={isSelected}
            editLayer={editLayer}
            setSelectedShapes={setSelectedShapes}
            setContextMenu={setContextMenu}
            isTripPointLinked={isTripPointLinked}
            allowColorFallback={allowColorFallback}
            isFixedRouteCaptureMode={isFixedRouteCaptureMode}
        />
    );
}

export function useRenderMarkers() {
    const context = useContext(AppContext);
    const editLayer = useEditLayer();

    if (!context) {
        throw new Error("useRenderMarkers must be used within the AppContext provider");
    }

    const { global } = context;
    const { mapLayers, setSelectedShapes } = global;

    return (setContextMenu: Function) => {
        return mapLayers
            .filter(layer => !layer.hidden)
            .flatMap((layer, layerIndex) => {
                const tripPointIds = new Set<string>();
                layer.shapes.forEach((shape) => {
                    if (
                        shape.type === 'line' &&
                        'originPointId' in shape &&
                        'destPointId' in shape &&
                        (shape as any).originPointId &&
                        (shape as any).destPointId
                    ) {
                        tripPointIds.add((shape as any).originPointId);
                        tripPointIds.add((shape as any).destPointId);
                    }
                });
                const hasLinkedTripPoints = tripPointIds.size > 0;
                return layer.shapes.map((shape, pointIndex) => {
                    if (shape.type === 'point' && !shape.hidden) {
                        const isSelected = global.selectedShapes.includes(shape.id);
                        return returnPointMarker(
                            shape,
                            layer,
                            layerIndex,
                            pointIndex,
                            isSelected,
                            editLayer,
                            setSelectedShapes,
                            setContextMenu,
                            tripPointIds.has(shape.id),
                            !hasLinkedTripPoints,
                            false
                        );
                    }
                    return null;
                });
            }
            );
    };
}

/**
 * Renders markers with pick mode logic for the map.
 * @param global - The global context object containing mapLayers and selectedShapes.
 * @param pickMode - The current pick mode ('start', 'end', or null).
 * @param handlePointPick - Function to handle picking a point in pick mode.
 * @param editLayer - Function to edit a layer.
 * @param setContextMenu - Function to set the context menu state.
 * @returns Array of Marker JSX elements.
 */
export function renderMarkersWithPick(
    global: any,
    pickMode: 'start' | 'end' | null,
    handlePointPick: (point: any) => boolean,
    editLayer: Function,
    setContextMenu: Function,
    isFixedRouteCaptureMode: boolean
) {
    // Reverse layers array so top layer in menu renders on top visually
    return global.mapLayers
        .filter((layer: any) => !layer.hidden)
        .slice()
        .reverse()
        .map((layer: any) => {
            const tripPointIds = new Set<string>();
            layer.shapes.forEach((shape: any) => {
                if (
                    shape.type === 'line' &&
                    'originPointId' in shape &&
                    'destPointId' in shape &&
                    shape.originPointId &&
                    shape.destPointId
                ) {
                    tripPointIds.add(shape.originPointId);
                    tripPointIds.add(shape.destPointId);
                }
            });
            const hasLinkedTripPoints = tripPointIds.size > 0;
            const visiblePointShapes = layer.shapes
                .slice()
                .reverse()
                .filter((shape: any) => shape.type === 'point' && !shape.hidden);
            if (visiblePointShapes.length === 0) {
                return null;
            }
            const markers = visiblePointShapes.map((shape: any) => {
                const isSelected = global.selectedShapes.includes(shape.id);
                return (
                    <Marker
                        key={`point-${shape.id}`}
                        position={new LatLng(shape.coordinates[0], shape.coordinates[1])}
                        icon={customMarker(shape, isSelected, {
                            isTripPointLinked: tripPointIds.has(shape.id),
                            allowColorFallback: !hasLinkedTripPoints,
                        })}
                        draggable={true}
                        eventHandlers={{
                            contextmenu: (e: any) => {
                                e.originalEvent.preventDefault();
                                setContextMenu({
                                    x: e.originalEvent.pageX,
                                    y: e.originalEvent.pageY,
                                    point: shape
                                });
                            },
                            click: (e: any) => {
                                if (isFixedRouteCaptureMode) {
                                    e.originalEvent.preventDefault();
                                    if (e.originalEvent.stopPropagation) {
                                        e.originalEvent.stopPropagation();
                                    }
                                    dispatchFixedRouteCaptureLeftClick(shape.coordinates[0], shape.coordinates[1]);
                                    return;
                                }

                                if (pickMode) {
                                    handlePointPick(shape);
                                    e.originalEvent.preventDefault();
                                    return;
                                }
                                // ... your normal selection logic ...
                            },
                            dragend: (e: any) => {
                                const { lat, lng } = e.target.getLatLng();
                                const updatedPoint = { ...shape, coordinates: [lat as number, lng as number] as [number, number] };
                                const updatedShapes = layer.shapes.map((s: any) => s.id === shape.id ? updatedPoint : s);
                                const updatedLayer = { ...layer, shapes: updatedShapes };
                                editLayer(updatedLayer, layer.layerId);
                            }
                        }}
                    />
                );
            });

            // Wrap dense layers (e.g. OSM bus stops) in a cluster group so the map
            // stays responsive. Small custom point sets render as plain markers to
            // preserve the existing UX for typical layers.
            if (visiblePointShapes.length > CLUSTER_THRESHOLD) {
                return (
                    <MarkerClusterGroup
                        key={`cluster-${layer.layerId}`}
                        chunkedLoading
                        maxClusterRadius={60}
                    >
                        {markers}
                    </MarkerClusterGroup>
                );
            }
            return <React.Fragment key={`layer-${layer.layerId}`}>{markers}</React.Fragment>;
        })
        .filter(Boolean);
}
